<script lang="ts" setup>
import type { PromoCta } from '#core/types'
import { Button } from '@forgd/ui-kit/button'
import { EmbeddedVideo } from '@forgd/ui-kit/blocks/embedded-video'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@forgd/ui-kit/dialog'
import { PlayIcon } from '@heroicons/vue/24/outline'

const props = withDefaults(defineProps<{
  heading?: string
  intro?: string
  items?: string[]
  ordered?: boolean
  ctas: PromoCta[]
  image: string
  imageAlt?: string
  imageClass?: string
  cropImageOnMobile?: boolean
}>(), {
  heading: '',
  ordered: false,
  items: () => [],
  imageAlt: '',
  imageClass: 'object-cover object-right',
  cropImageOnMobile: false,
})

const activeVideo = ref('')

const embedUrl = computed(() => {
  if (!activeVideo.value) {
    return ''
  }
  const url = new URL(activeVideo.value)
  const videoId = url.searchParams.get('v') ?? url.pathname.split('/').pop()
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&autoplay=1`
})

const slots = useSlots()
const hasIntro = computed(() => Boolean(props.intro || slots.intro))
</script>

<template>
  <section
    class="relative overflow-hidden rounded-lg bg-secondary-subtle bg-cover bg-[center_0%]"
    :style="{ backgroundImage: 'url(/images/home/promo-bg.jpg)' }"
  >
    <div class="grid grid-cols-1 gap-4 md:grid-cols-[1.1fr_1fr]">
      <div class="flex flex-col justify-center gap-5 px-4 py-5 md:p-8 max-w-[550px]">
        <h3 class="text-lg leading-normal text-primal font-regular md:text-3xl md:leading-tight max-w-[510px]">
          <slot name="heading">
            {{ heading }}
          </slot>
        </h3>

        <div v-if="hasIntro || items.length" class="flex flex-col gap-2.5">
          <div v-if="hasIntro" class="text-sm text-secondary">
            <slot name="intro">
              {{ intro }}
            </slot>
          </div>
          <component
            :is="ordered ? 'ol' : 'ul'"
            v-if="items.length"
            class="space-y-1.5 pl-5 text-sm text-secondary"
            :class="ordered ? 'list-decimal' : 'list-disc'"
          >
            <li v-for="(item, i) in items" :key="i" class="pl-1">
              {{ item }}
            </li>
          </component>
        </div>

        <div class="mt-1 flex flex-wrap items-center gap-2 md:gap-3">
          <template v-for="cta in ctas" :key="cta.label">
            <Button
              v-if="cta.videoUrl"
              :variant="cta.variant ?? 'default'"
              size="sm"
              :class="ctas.length === 1 ? 'w-full md:w-auto' : ''"
              @click="activeVideo = cta.videoUrl ?? ''"
            >
              {{ cta.label }}
              <PlayIcon v-if="cta.icon === 'play'" />
            </Button>
            <Button
              v-else
              as-child
              :variant="cta.variant ?? 'default'"
              size="sm"
              :class="ctas.length === 1 ? 'w-full md:w-auto' : ''"
            >
              <NuxtLink :to="cta.to">
                {{ cta.label }}
                <PlayIcon v-if="cta.icon === 'play'" />
              </NuxtLink>
            </Button>
          </template>
        </div>
      </div>

      <div class="relative overflow-hidden" :class="cropImageOnMobile ? 'aspect-[343/309] md:aspect-auto' : ''">
        <img
          :src="image"
          :alt="imageAlt"
          loading="lazy"
          :class="[cropImageOnMobile ? 'absolute inset-0 size-full' : 'h-auto w-full md:absolute md:inset-0 md:size-full', imageClass]"
        >
      </div>
    </div>

    <Dialog :open="!!activeVideo" @update:open="(value) => !value && (activeVideo = '')">
      <DialogContent hide-close class="!p-0 w-[60vw] bg-black max-w-none overflow-hidden rounded-2xl sm:rounded-2xl border-0">
        <DialogTitle class="sr-only">
          Watch the demo
        </DialogTitle>
        <DialogDescription class="sr-only">
          Product demo video.
        </DialogDescription>
        <EmbeddedVideo v-if="activeVideo" :src="embedUrl" title="Product demo video" aspect-ratio="16/9" />
      </DialogContent>
    </Dialog>
  </section>
</template>
